import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { getTour } from '@/lib/onboarding/tours';
import {
  ArrowLeft, ArrowRight, X, Crown, Sparkles,
  Flame, Zap, Trophy, Users, Calendar, BookOpen,
  Plus, Link, Calculator, Download, ShoppingCart,
  BarChart3, Video, Radio, Bell, Monitor,
  LayoutDashboard, DollarSign, Heart, Coins,
  User, CheckCircle, Award, Brain, Camera,
  Wand2, Snowflake, Star, Gift, UserPlus, ChefHat,
  Shield,
} from 'lucide-react';

const ICON_MAP: Record<string, React.ElementType> = {
  ChefHat, Sparkles, Flame, Zap, Trophy, Users, Calendar,
  BookOpen, Plus, Link, Calculator, Download, ShoppingCart,
  BarChart3, Video, Radio, Bell, Monitor, LayoutDashboard,
  DollarSign, Heart, Coins, User, CheckCircle, Award, Brain,
  Camera, Wand2, Snowflake, Star, Gift, UserPlus, Crown, Shield,
};

const ROLE_BADGE_MAP: Record<string, { icon: React.ElementType; labelKey: string }> = {
  'helper-onboarding': { icon: Heart, labelKey: 'tour.helper.roleBadge' },
  'admin-onboarding': { icon: Shield, labelKey: 'tour.admin.roleBadge' },
  'moderator-onboarding': { icon: Shield, labelKey: 'tour.moderator.roleBadge' },
};

export function TourCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    activeTour, currentStep, totalSteps, currentStepData,
    nextStep, prevStep, skipTour,
  } = useOnboarding();
  const { effectiveTier } = useSubscription();

  if (!activeTour || !currentStepData) return null;

  const tour = getTour(activeTour);
  if (!tour) return null;

  const Icon = ICON_MAP[currentStepData.icon] ?? Sparkles;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Tier-aware logic
  const tierRank = { free: 0, pro: 1, chef_master: 2 };
  const userRank = tierRank[effectiveTier as keyof typeof tierRank] ?? 0;
  const stepRank = tierRank[currentStepData.tier as keyof typeof tierRank] ?? 0;
  const isLocked = stepRank > userRank;
  const showUpgradeTeaser = isLocked && currentStepData.upgradeTeaser;

  const handleUpgrade = () => {
    navigate('/pricing');
    skipTour();
  };

  return (
    <>
      {/* Mobile: anchored to bottom; Desktop: floating bottom-right */}
      <div
        className="fixed z-[9999] bottom-0 left-0 right-0 md:bottom-6 md:right-6 md:left-auto md:w-[380px] pointer-events-auto"
        style={{ maxHeight: '60vh' }}
        onClick={e => e.stopPropagation()}
      >
        <Card className="rounded-t-2xl md:rounded-2xl border-border/60 shadow-2xl overflow-hidden">
          <CardContent className="p-0">
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                {(() => {
                  const roleBadge = activeTour ? ROLE_BADGE_MAP[activeTour] : null;
                  if (roleBadge) {
                    const RoleIcon = roleBadge.icon;
                    return (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-600 text-[10px] shrink-0 gap-1">
                        <RoleIcon className="h-3 w-3" />
                        {t(roleBadge.labelKey)}
                      </Badge>
                    );
                  }
                  return null;
                })()}
                <Badge variant="secondary" className="bg-primary/10 text-primary text-[10px] shrink-0">
                  {t('tour.ui.tourBadge', {
                    tour: t(tour.labelKey),
                    current: currentStep + 1,
                    total: totalSteps,
                    defaultValue: `{{tour}} · Step {{current}} of {{total}}`,
                  })}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={skipTour}
                className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Progress bar */}
            <Progress value={progress} className="h-0.5 rounded-none mx-4" />

            {/* Step body */}
            <div className="px-4 py-4 space-y-3 overflow-y-auto" style={{ maxHeight: '40vh' }}>
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-bold leading-tight mb-1">
                    {t(currentStepData.titleKey, { defaultValue: currentStepData.titleKey })}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(currentStepData.descriptionKey, { defaultValue: currentStepData.descriptionKey })}
                  </p>
                </div>
              </div>

              {/* Upgrade teaser */}
              {showUpgradeTeaser && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-amber-500 text-sm font-medium">
                    <Crown className="h-4 w-4" />
                    {currentStepData.tier === 'chef_master'
                      ? t('tour.ui.upgradeChefMaster', { defaultValue: 'Upgrade to Chef Master to unlock' })
                      : t('tour.ui.upgradePro', { defaultValue: 'Upgrade to Pro to unlock' })}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-amber-500/40 text-amber-600 hover:bg-amber-500/10 text-xs h-7"
                    onClick={handleUpgrade}
                  >
                    {t('tour.ui.seePlans', { defaultValue: 'See plans & pricing' })}
                  </Button>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between px-4 pb-4 pt-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={prevStep}
                disabled={currentStep === 0}
                className="gap-1 text-xs"
              >
                <ArrowLeft className="h-3 w-3" />
                {t('tour.ui.back', { defaultValue: 'Back' })}
              </Button>

              {/* Dot indicators */}
              <div className="flex gap-1">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 w-1.5 rounded-full transition-colors ${
                      i === currentStep
                        ? 'bg-primary'
                        : i < currentStep
                        ? 'bg-primary/40'
                        : 'bg-muted-foreground/30'
                    }`}
                  />
                ))}
              </div>

              <Button
                size="sm"
                onClick={nextStep}
                className="gap-1 text-xs"
              >
                {currentStep === totalSteps - 1
                  ? t('tour.ui.finish', { defaultValue: "Let's Go!" })
                  : t('tour.ui.next', { defaultValue: 'Next' })}
                {currentStep < totalSteps - 1 && <ArrowRight className="h-3 w-3" />}
              </Button>
            </div>

            {/* Skip link */}
            <div className="text-center pb-3">
              <Button
                variant="link"
                size="sm"
                onClick={skipTour}
                className="text-[10px] text-muted-foreground h-auto p-0"
              >
                {t('tour.ui.skip', { defaultValue: 'Skip Tour' })}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
